import { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { UsePortfolioChartDataParams } from 'src/components/PortfolioPage/PortfolioTradeSubPage/types';
import {
	buildCalendarPnlBaseData,
	buildCalendarPnlLiveData,
} from 'src/components/PortfolioPage/PortfolioTradeSubPage/utils/buildCalendarPnlData';

interface Params extends UsePortfolioChartDataParams {
	month: Dayjs;
}

export default function usePortfolioCalendarPnlData({
	enabled,
	snapshots,
	metrics,
	currentCumulativeFunding,
	currentUnrealizedPnl,
	includeFeesAndFunding,
	realizedPnlOnly,
	month,
}: Params) {
	const calendarBase = useMemo(() => {
		return buildCalendarPnlBaseData({
			enabled,
			snapshots,
			includeFeesAndFunding,
			realizedPnlOnly,
			month,
		});
	}, [enabled, snapshots, includeFeesAndFunding, realizedPnlOnly, month]);

	const calendarLive = useMemo(() => {
		return buildCalendarPnlLiveData({
			enabled,
			metrics,
			currentCumulativeFunding,
			currentUnrealizedPnl,
			includeFeesAndFunding,
			realizedPnlOnly,
			latestSnapshotTotalPnl: calendarBase.latestSnapshotTotalPnl,
			latestSnapshotFeesAndFunding: calendarBase.latestSnapshotFeesAndFunding,
		});
	}, [
		enabled,
		metrics,
		currentCumulativeFunding,
		currentUnrealizedPnl,
		includeFeesAndFunding,
		realizedPnlOnly,
		calendarBase.latestSnapshotTotalPnl,
		calendarBase.latestSnapshotFeesAndFunding,
	]);

	return useMemo(() => {
		const pnlByDay = new Map(calendarBase.pnlByDay);

		if (calendarLive.day) {
			pnlByDay.set(calendarLive.day, calendarLive.value);
		}

		let numWinningDays = 0;
		let numLosingDays = 0;
		let numFlatDays = 0;
		for (const value of pnlByDay.values()) {
			if (value.pnl > 0) {
				numWinningDays++;
			} else if (value.pnl < 0) {
				numLosingDays++;
			} else {
				numFlatDays++;
			}
		}

		return {
			pnlByDay,
			daysInMonth: calendarBase.daysInMonth,
			startWeekday: calendarBase.startWeekday,
			numWinningDays,
			numLosingDays,
			numFlatDays,
		};
	}, [calendarBase, calendarLive]);
}
